import React, { useLayoutEffect, useRef, useState } from "react";
import "./styles.css";
import { ReactComponent as EyeIcon } from "../../../assets/icons/eye.svg";
import { ReactComponent as EyeSlashIcon } from "../../../assets/icons/eye-slash.svg";
import { cleanNumber, formatNumber } from "./utils";

export const Input = ({
                        error,
                        errorMessage,
                        register = {},
                        value,
                        onChange,
                        onBlur,
                        name,
                        type,
                        placeholder,
                        label,
                        autoComplete = "off",
                        inputMode,
                        min,
                        max,
                        normalizeDecimal = false,
                        variant,
                        containerClassName = "",
                        inputClassName = "",
                        labelClassName = "",
                      }) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const digitsBeforeCaretRef = useRef<number | null>(null);
  const isPassword = type === "password";
  const isNumber = type === "number";
  const isPricingModernVariant = variant === "pricing-modern";
  const autoCompleteValue = isPassword && autoComplete === "new-password" ? "new-password" : "off";
  const registerRef = register?.ref;
  const registerOnChange = register?.onChange;
  const registerOnBlur = register?.onBlur;
  const isControlled = value !== undefined;

  const normalizeDecimalValue = (rawValue) => {
    const withDot = String(rawValue ?? "").replace(/,/g, ".");
    const cleaned = withDot.replace(/[^\d.]/g, "");
    const [integerPart = "", ...decimalParts] = cleaned.split(".");

    if (!decimalParts.length) {
      return integerPart;
    }

    return `${integerPart}.${decimalParts.join("")}`;
  };

  // Preserve caret position when numeric values are reformatted with separators.
  useLayoutEffect(() => {
    if (!isNumber) return;
    if (digitsBeforeCaretRef.current === null) return;
    if (!inputRef.current || document.activeElement !== inputRef.current) return;

    const digitsBeforeCaret = digitsBeforeCaretRef.current;
    digitsBeforeCaretRef.current = null;

    if (digitsBeforeCaret <= 0) {
      inputRef.current.setSelectionRange(0, 0);
      return;
    }

    let digitsCount = 0;
    let caretPosition = inputRef.current.value.length;

    for (let i = 0; i < inputRef.current.value.length; i += 1) {
      if (/\d/.test(inputRef.current.value[i])) {
        digitsCount += 1;
      }

      if (digitsCount >= digitsBeforeCaret) {
        caretPosition = i + 1;
        break;
      }
    }

    inputRef.current.setSelectionRange(caretPosition, caretPosition);
  }, [isNumber, value]);

  const handleChange = (e) => {
    const raw = e.target.value;

    if (isNumber) {
      const caretPosition = e.target.selectionStart ?? raw.length;
      digitsBeforeCaretRef.current = cleanNumber(raw.slice(0, caretPosition)).length;
    }

    const cleaned = isNumber
        ? cleanNumber(raw)
        : normalizeDecimal
            ? normalizeDecimalValue(raw)
            : raw;

    if (typeof registerOnChange === "function") {
      registerOnChange({
        ...e,
        target: {
          ...e.target,
          name: register?.name ?? name,
          value: cleaned,
        },
      });
    }

    if (typeof onChange === "function") {
      onChange(isNumber ? Number(cleaned) : cleaned);
    }
  };

  const handleBlur = (e) => {
    if (typeof registerOnBlur === "function") {
      registerOnBlur(e);
    }

    if (typeof onBlur === "function") {
      onBlur(e);
    }
  };

  const inputIcon = passwordVisible ? (
      <EyeSlashIcon width="100%" height="100%" stroke="#818c99" />
  ) : (
      <EyeIcon width="100%" height="100%" stroke="#818c99" />
  );

  // Если значение равно 0, оставляем его; если undefined, null или '', то передаем пустую строку
  const formattedValue = isNumber ? formatNumber(value === 0 ? 0 : (value || "")) : value;

  return (
      <>
        {label && !isPricingModernVariant && (
            <div className={`input_label ${labelClassName}`.trim()}>{label}</div>
        )}
        <div
            className={`input_container ${isPricingModernVariant ? "input_container-pricing-modern" : ""} ${error ? "input_container-error" : ""} ${containerClassName}`.trim()}
        >
          {label && isPricingModernVariant && (
              <div
                  className={`input_label input_label-pricing-modern ${labelClassName}`.trim()}
              >
                {label}
              </div>
          )}
          <input
              className={`input_main ${isPricingModernVariant ? "input_main-pricing-modern" : ""}${error ? " input_error" : ""} ${inputClassName}`.trim()}
              {...register}
              ref={(node) => {
                inputRef.current = node;
                if (typeof registerRef === "function") {
                  registerRef(node);
                } else if (registerRef) {
                  registerRef.current = node;
                }
              }}
              name={name}
              type={passwordVisible ? "text" : isNumber ? "text" : type} // override для форматирования
              inputMode={inputMode}
              min={min}
              max={max}
              placeholder={placeholder}
              value={isControlled ? formattedValue : undefined}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete={autoCompleteValue}
          />
          {isPassword && (
              <div
                  className="input_icon"
                  onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {inputIcon}
              </div>
          )}
        </div>
        {errorMessage && (
            <div className="input_error-message">{errorMessage}</div>
        )}
      </>
  );
};
